/* ═══ PRINTED WORDING THE TOOL WRITES ITSELF (2026-11-02, docs/quotes/ai-box-plan.md) ═══
   One map per quote, `printWording`. For each key: absent or null = generated as always,
   "" = removed, text = custom. Quotes with no map print exactly as before.

   Reset writes null, never deletes the key: quote saves merge (dataService setDoc with
   merge:true, and buildQuoteSaveRecord spreads the old record underneath), so a deleted
   key would come back on reload. Every write carries all the keys for the same reason.

   Pure, with no imports, so builderHelpers can read it. Amounts come in from the caller. */

export const PRINT_WORDING_KEYS = ["paymentSentence", "rawModels", "rawFiles", "lowPolyExclusions", "usageRights", "proBonoNote", "estimateNote"];

export const PRINT_WORDING_LABELS = {
  paymentSentence: "Payment sentence",
  rawModels: "Raw models turnaround sentence",
  rawFiles: "Raw files sentence",
  lowPolyExclusions: "Low poly exclusions sentence",
  usageRights: "Usage rights sentence",
  proBonoNote: "Pro bono line",
  estimateNote: "Estimate line",
};

export const ESTIMATE_NOTE = "Final invoice based on actual session quantities";

export const normalizePrintWording = (map) => {
  const out = {};
  for (const k of PRINT_WORDING_KEYS) {
    const v = map && typeof map === "object" ? map[k] : null;
    out[k] = typeof v === "string" ? v : null;
  }
  return out;
};

/** The stored value of a key: null (generated), "" (removed) or the custom text. */
export const wordingOf = (q, key) => {
  const v = q?.printWording?.[key];
  return typeof v === "string" ? v : null;
};

/** What prints for a key whose generated text is `generated`. "" means nothing prints. */
export const wordingOr = (q, key, generated) => {
  const v = wordingOf(q, key);
  return v === null ? generated : v;
};

export const hasCustomWording = (q) => PRINT_WORDING_KEYS.some((k) => wordingOf(q, k) !== null);

const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

/** The custom payment sentence with its placeholders filled. null means print the
 *  generated sentence: nothing custom, or a placeholder that has no value now, because a
 *  raw {deposit} must never print. "" means the sentence is removed. */
export function customPaymentSentence(q, depositAmount, total) {
  const text = wordingOf(q, "paymentSentence");
  if (text === null || text === "") return text;
  if (text.includes("{deposit}") && (depositAmount === null || depositAmount === undefined)) return null;
  return text.replace(/\{total\}/g, money(total)).replace(/\{deposit\}/g, money(depositAmount ?? 0));
}

// $500, USD 500, 500 USD, 500 dollars, 500-dollar.
export const hasTypedDollars = (text) => /\$\s?\d|\busd[\s-]?\d|\d[\s-]?(?:usd|dollars?|bucks)\b/i.test(String(text));
const placeholders = (text) => String(text).match(/\{[^}]*\}/g) || [];

/** Refuses wording that can't print right, and notes a typed dollar figure. depositType
 *  is getDeposit(q).type, which knows about old quotes. */
export function checkWordingText(key, text, depositType) {
  const found = placeholders(text);
  if (key !== "paymentSentence" && found.length) {
    return { refuse: `${PRINT_WORDING_LABELS[key]} can't use ${found.join(", ")}. Only the payment sentence fills in amounts.` };
  }
  const unknown = found.filter((p) => p !== "{total}" && p !== "{deposit}");
  if (unknown.length) return { refuse: `Payment wording can use {total} and {deposit}, not ${unknown.join(", ")}.` };
  if (key === "paymentSentence" && found.includes("{deposit}")) {
    if (depositType === "full_session") return { refuse: "A full session deposit has no computed dollar amount, so {deposit} can't fill in. Say it in words." };
    if (depositType === "none") return { refuse: "This quote has no deposit, so {deposit} has no value." };
  }
  const notes = [];
  if (key === "paymentSentence" && hasTypedDollars(text)) {
    notes.push("This wording has a typed dollar figure, which won't follow the total. {total} and {deposit} fill in by themselves.");
  }
  return { notes };
}

/** Every money input: two lines can trade dollars at the same total, so the total alone
 *  isn't enough. Computed from the quote, never from a reply. */
export function moneyFingerprint(q) {
  const input = JSON.stringify({
    lines: (q?.lineItems || []).map((li) => [Number(li.qty) || 0, Number(li.rate) || 0, li.discount ? [li.discount.type, Number(li.discount.value) || 0] : null]),
    discounts: (q?.quoteDiscounts || []).map((d) => [d.type, Number(d.value) || 0]),
    deposit: [q?.deposit?.type || (q?.requiresDeposit ? "full_session" : "none"), q?.deposit?.value ?? null],
  });
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) h = (h * 33 + input.charCodeAt(i)) % 4294967291;
  return `m${h.toString(36)}`;
}

/** Back to the tool's own wording, by the merge rule above. */
export const resetWordingPatch = (q, key) => ({
  printWording: { ...normalizePrintWording(q?.printWording), [key]: null },
  ...(key === "paymentSentence" ? { printWordingMoneyKey: null } : {}),
});
